/**
 * Shows the DDL a clone would run, generated in the main process by the same
 * plan the executor follows (`electron/clone-table/cloneScriptPlan.ts`) — so the
 * preview can't drift from what pressing Clone actually does.
 */

import { useCallback } from 'react';
import { FileCode2, Clipboard } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { toast } from '../../../components/ui/toaster';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { writeClipboardText } from '@/lib/clipboard';
import { useAnalytics } from '../../../core/analytics/useAnalytics';

type CloneScriptPreviewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetTableName: string;
  script?: string;
  error?: string;
  isLoading: boolean;
};

export function CloneScriptPreviewDialog({
  open,
  onOpenChange,
  targetTableName,
  script,
  error,
  isLoading,
}: CloneScriptPreviewDialogProps) {
  const { track } = useAnalytics();

  // Await the write before claiming success: a failed write resolves false,
  // and reporting "copied" over a failed write (plus recording it as a
  // successful copy in analytics) tells the user the script is on their clipboard
  // when it isn't.
  const handleCopy = useCallback(async () => {
    if (!script) return;
    const ok = await writeClipboardText(script);
    if (!ok) {
      toast.error({ title: 'Could not copy the script to the clipboard' });
      return;
    }
    track('feature:clone_table_preview_copy');
    toast.success({ title: 'Script copied to clipboard' });
  }, [script, track]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="clone-script-preview-dialog"
        className="sm:max-w-[750px]"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <FileCode2 className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <DialogTitle>Clone Script</DialogTitle>
              <DialogDescription className="mt-1">
                What Jam SQL will run to create &quot;{targetTableName}&quot;
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-2">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-10">
              <RandomDotmSpinner size={28} className="text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Reading the source table…</p>
            </div>
          ) : error ? (
            <div
              className="p-3 rounded-md bg-destructive/10 border border-destructive/30"
              data-testid="clone-script-preview-error"
            >
              <p className="text-sm text-destructive">{error}</p>
            </div>
          ) : (
            <pre
              className="max-h-[50vh] overflow-auto rounded-md bg-muted p-3 text-xs leading-relaxed font-mono whitespace-pre"
              data-testid="clone-script-preview-sql"
            >
              {script}
            </pre>
          )}
        </div>

        <DialogFooter>
          {script && !isLoading && (
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleCopy()}
              data-testid="clone-script-preview-copy"
            >
              <Clipboard className="h-4 w-4 mr-2" />
              Copy
            </Button>
          )}
          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="clone-script-preview-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
